// The boot ordering (sdk ADR-013).
import type { SdkBootstrap } from '../bootstrap.js';
import { isDaemonUnresolved } from '../bootstrap.js';
import type { ShogunChopAuthSnapshot } from '../types/shogunchop.js';
import type { BudgetedOutcome } from './budget.js';
import { LIFECYCLE_BUDGET_MS, withBudget } from './budget.js';
import { emitLifecycleEvent } from './observability.js';

export type SnapshotFailure = 'budget_expired' | 'unreachable';

export type BootState =
  | { state: 'resolving' }
  | { state: 'refused' }
  | { state: 'snapshotting' }
  | {
      state: 'snapshot_unavailable';
      cause: SnapshotFailure;
      elapsedMs: number;
      budgetMs: number;
      detail: string | null;
    }
  | { state: 'settled'; snapshot: ShogunChopAuthSnapshot | null }
  | { state: 'failed'; cause: unknown };

/** The two states a snapshot attempt can reach (sdk ADR-013). */
export type SnapshotOutcomeState = Extract<
  BootState,
  { state: 'settled' } | { state: 'snapshot_unavailable' }
>;

/** The publication point (sdk ADR-013). */
export function publishesClient(state: BootState): boolean {
  return state.state === 'settled';
}

/** A terminal state the user must be offered a Retry out of. */
export function isTerminalRefusal(state: BootState): boolean {
  return (
    state.state === 'refused' || state.state === 'snapshot_unavailable' || state.state === 'failed'
  );
}

export type BootRefusalCode = 'daemon-missing' | 'snapshot-unavailable';

/** The refusal a boot throws instead of publishing (sdk ADR-013). */
export class BootRefused extends Error {
  readonly code: BootRefusalCode;

  constructor(code: BootRefusalCode, message: string) {
    super(message);
    this.name = 'BootRefused';
    this.code = code;
  }
}

/** The arm is the bootstrap's answer, never the host's own type (sdk ADR-031). */
export function bootAfterResolve(bootstrap: SdkBootstrap): BootState {
  if (isDaemonUnresolved(bootstrap.daemonUrl)) return { state: 'refused' };
  if (bootstrap.daemonUrl === null) return { state: 'settled', snapshot: null };
  return { state: 'snapshotting' };
}

/** A budget expiry is a named refusal, never a guessed phase (sdk ADR-013). */
export function bootAfterSnapshot(
  outcome: BudgetedOutcome<ShogunChopAuthSnapshot>,
  budgetMs: number,
): SnapshotOutcomeState {
  if (outcome.kind === 'settled') {
    return { state: 'settled', snapshot: outcome.value };
  }
  return {
    state: 'snapshot_unavailable',
    cause: outcome.kind === 'expired' ? 'budget_expired' : 'unreachable',
    elapsedMs: outcome.elapsedMs,
    budgetMs,
    detail: outcome.kind === 'failed' ? causeText(outcome.cause) : null,
  };
}

export function bootSequence(
  bootstrap: SdkBootstrap,
  outcome: BudgetedOutcome<ShogunChopAuthSnapshot> | null,
  budgetMs: number = LIFECYCLE_BUDGET_MS,
): BootState[] {
  const resolved = bootAfterResolve(bootstrap);
  const sequence: BootState[] = [{ state: 'resolving' }, resolved];
  if (resolved.state !== 'snapshotting') return sequence;
  if (outcome === null) {
    throw new Error(
      'bootSequence: a desktop realm with a daemon reaches `snapshotting`, so it needs a ' +
        'snapshot outcome — passing none asks for a sequence that cannot exist',
    );
  }
  sequence.push(bootAfterSnapshot(outcome, budgetMs));
  return sequence;
}

export interface DaemonBootClient {
  readonly shogunchop: { authState(): Promise<ShogunChopAuthSnapshot> };
  applyDaemonSnapshot(snapshot: ShogunChopAuthSnapshot): Promise<void>;
  startDaemonMirror(sinceSeq?: number): void;
}

export interface DaemonBootOptions {
  /** The realm stem — the correlating dimension (sdk ADR-027). */
  realm: string;
  daemonUrl: string;
  budgetMs?: number;
}

/** Settle the daemon's phase into the holder before publication (sdk ADR-013). */
export async function settleDaemonBoot(
  client: DaemonBootClient,
  options: DaemonBootOptions,
): Promise<SnapshotOutcomeState> {
  const budgetMs = options.budgetMs ?? LIFECYCLE_BUDGET_MS;
  const outcome = await withBudget(client.shogunchop.authState(), budgetMs);
  const state = bootAfterSnapshot(outcome, budgetMs);
  if (state.state === 'snapshot_unavailable') {
    emitLifecycleEvent('lifecycle.snapshot_unavailable', {
      realm: options.realm,
      daemon_address: options.daemonUrl,
      elapsed: state.elapsedMs,
      budget: state.budgetMs,
      cause: state.cause,
      detail: state.detail,
    });
    return state;
  }
  if (state.snapshot === null) {
    throw new Error('settleDaemonBoot: a settled daemon boot carries a snapshot');
  }
  await client.applyDaemonSnapshot(state.snapshot);
  client.startDaemonMirror(state.snapshot.seq);
  return state;
}

/** One in-flight attempt, however many callers ask. */
export class SingleFlight<T> {
  private inFlight: Promise<T> | null = null;

  run(work: () => Promise<T>): Promise<T> {
    if (this.inFlight !== null) return this.inFlight;
    const attempt = work();
    this.inFlight = attempt;
    return attempt.finally(() => {
      if (this.inFlight === attempt) this.inFlight = null;
    });
  }
}

function causeText(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
